import "../styles/plan-selection.scss";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { Context } from "../context/AuthContext";
import { UserState } from "../appState";
import { Check } from "@mui/icons-material";
import logo from "../assets/img/FFF_Logo_Orange_Horizontal.png";
import ProtectedRoute from "../components/guards/protected-route";
import {
  ANNUAL_PRICE,
  PromoDetails,
  checkPromoCode,
  discountLabel as getDiscountLabel,
  discountTerm,
  discountedPrice as getDiscountedPrice,
  formatPrice,
} from "../utils/pricing";

const orderApiUrl = `${process.env.REACT_APP_API_URL}/api/orders`;

const PlanSelection = () => {
  const navigate = useNavigate();
  const user = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const promoCode = localStorage.getItem("fff_discount_code");
  const hasPromo = !!promoCode;
  const [promo, setPromo] = useState<PromoDetails | null>(null);

  useEffect(() => {
    if (!promoCode) return;
    checkPromoCode(promoCode)
      .then((result) => {
        if (result.valid && result.details) setPromo(result.details);
      })
      // No details: fall back to the generic "promo applied" badge
      .catch(() => {});
  }, [promoCode]);

  const discountedPrice = promo ? getDiscountedPrice(promo) : null;
  const discountLabel = promo ? getDiscountLabel(promo) : "";

  const handleStartTrial = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const priceId = process.env.REACT_APP_STRIPE_PRICE_ID || "price_1UQCEwGL1dDBQUpktIO7Mnqx";
      const UserToken = localStorage.getItem("ffftoken");
      // Use Firebase auth user as source of truth; Zustand may not be hydrated yet
      const UID = user?.user?.uid || UserState.getState().uid || localStorage.getItem("fffuser") || "";

      const res = await fetch(`${orderApiUrl}/createCheckoutSession`, {
        method: "POST",
        headers: new Headers({
          "content-type": "application/json",
          "authorization": `${UserToken}`,
          "x-user-id": UID,
        }),
        body: JSON.stringify({
          priceId,
          user: UID,
          email: user?.user?.email || UserState?.getState()?.email || "",
          avantlink_avad: localStorage.getItem("avantlink_avad") || "",
          avantlink_visitor_id: localStorage.getItem("avantlink_visitor_id") || "",
          avantlink_referrer: localStorage.getItem("avantlink_referrer") || "",
          avantlink_landing_url: localStorage.getItem("avantlink_landing_url") || "",
          user_agent: navigator.userAgent,
          promoCode: localStorage.getItem("fff_discount_code") || "",
        }),
      });

      if (res.status === 200) {
        const data = await res.json();
        if (data?.data) {
          window.location.href = data.data;
          return;
        }
      } else {
        const errBody = await res.json().catch(() => ({}));
        console.error("Checkout session error", res.status, errBody);
        const errText = (errBody?.error || errBody?.message || "").toLowerCase();
        if (errText.includes("coupon") || errText.includes("promo") || errText.includes("discount")) {
          localStorage.removeItem("fff_discount_code");
          setErrorMsg("That discount code is invalid or expired. It has been removed. Click the button again to continue without it.");
        } else {
          setErrorMsg("Something went wrong. Please try again.");
        }
      }
    } catch (err: any) {
      setErrorMsg(err?.message || "An error occurred");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      navigate("/");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <ProtectedRoute>
      <div className="plan-selection-page">
        <div className="plan-selection-content">
          <div className="plan-selection-header">
            <img src={logo} alt="FlyFishFinder" className="plan-logo" />
          </div>

          <div className="plan-selection-body">
            <h1 className="plan-title">Start Your 7-Day Free Trial</h1>
            <p className="plan-subtitle">
              Get full access to FlyFishFinder Pro. $0 today. Cancel anytime during your trial.
            </p>

            <div className="plan-card active">
              <div className="plan-card-header">
                <div className="plan-card-badge">MOST POPULAR</div>
                <h2 className="plan-card-name">Pro Nationwide</h2>
                <div className="plan-card-pricing">
                  <span className="plan-price-free">FREE</span>
                  <span className="plan-price-duration">for 7 days</span>
                </div>
                {discountedPrice !== null ? (
                  <p className="plan-price-after">
                    Then <s>{formatPrice(ANNUAL_PRICE)}</s> <strong>{formatPrice(discountedPrice)}</strong>
                    {promo && discountTerm(promo)}
                  </p>
                ) : (
                  <p className="plan-price-after">
                    Then <strong>{formatPrice(ANNUAL_PRICE)}</strong>/year
                  </p>
                )}
                {hasPromo && (
                  <div className="plan-promo-badge">
                    <span className="plan-promo-check">✓</span>
                    {discountLabel
                      ? `${promoCode} applied: ${discountLabel}`
                      : "Promo code applied. Discount secured at checkout"}
                  </div>
                )}
              </div>

              <div className="plan-card-features">
                <div className="plan-feature">
                  <span className="plan-check"><Check /></span>
                  <span>Top Waters: nearby rivers ranked right now by live flow, water temp, wind &amp; barometric pressure</span>
                </div>
                <div className="plan-feature">
                  <span className="plan-check"><Check /></span>
                  <span>Live gages &amp; flow alerts when your river hits your ideal CFS</span>
                </div>
                <div className="plan-feature">
                  <span className="plan-check"><Check /></span>
                  <span>Bite forecast for your area: the next 48 hours and your best window to fish</span>
                </div>
                <div className="plan-feature">
                  <span className="plan-check"><Check /></span>
                  <span>Public access mapped: easements, prime access spots, ramps &amp; public lands across the entire country</span>
                </div>
                <div className="plan-feature">
                  <span className="plan-check"><Check /></span>
                  <span>Species Mapper, wild trout &amp; Gold Medal waters</span>
                </div>
              </div>

              <button
                className="primary fill hover-shadow plan-start-btn"
                onClick={handleStartTrial}
                disabled={loading}
              >
                {loading ? "Loading..." : "Try Pro Free · $0 Today"}
              </button>

              {!!errorMsg && <p className="plan-error">{errorMsg}</p>}

              <p className="plan-disclaimer">
                You will not be charged during your 7-day trial. Cancel anytime, no questions asked.
              </p>
            </div>
          </div>

          <div className="plan-selection-footer">
            <button className="plan-logout-btn" onClick={handleSignOut}>
              Log Out
            </button>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default PlanSelection;
